'use client';

import { useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
    Button,
    SingleInput,
    Toggle as ToggleUI,
    Typography,
    type ButtonProps,
    type ToggleProps as ToggleUiProps,
    type TypographyProps,
} from '@/shared-ui/atoms';

type RootProps = ComponentPropsWithRef<'section'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <section
        data-slot="birthday-card"
        ref={ref}
        className={cn('flex w-full flex-col gap-x-4  bg-background p-4', className)}
        {...props}
    />
);
Root.displayName = 'BirthdayCard.Root';

type TitleProps = Omit<TypographyProps, 'as' | 'variant'> & {
    as?: TypographyProps['as'];
    variant?: TypographyProps['variant'];
};

const Title = ({ as = 'h2', variant = 'h4', className, ref, children, ...props }: TitleProps) => (
    <Typography
        data-slot="birthday-card-title"
        as={as}
        variant={variant}
        ref={ref as never}
        className={cn('font-normal', className)}
        {...props}>
        {children}
    </Typography>
);
Title.displayName = 'BirthdayCard.Title';

type ContentProps = ComponentPropsWithRef<'div'>;

const Content = ({ className, ref, ...props }: ContentProps) => (
    <div
        data-slot="birthday-card-content"
        ref={ref}
        className={cn('flex flex-wrap items-start gap-x-4 mt-4', className)}
        {...props}
    />
);
Content.displayName = 'BirthdayCard.Content';

type ImageProps = Omit<ComponentPropsWithRef<'img'>, 'src'> & {
    src: string;
};

const Image = ({ className, ref, alt = '', src, ...props }: ImageProps) => (
    <img
        data-slot="birthday-card-image"
        src={src}
        alt={alt}
        ref={ref}
        className={cn('h-[79px] w-[79px] shrink-0 object-cover', className)}
        loading="lazy"
        decoding="async"
        {...props}
    />
);
Image.displayName = 'BirthdayCard.Image';

type MessageProps = ComponentPropsWithRef<'p'>;

const Message = ({ className, ref, children, ...props }: MessageProps) => (
    <Typography
        data-slot="birthday-card-message"
        as="p"
        variant="p"
        ref={ref as never}
        className={cn('min-w-0 flex-1 font-light', className)}
        {...props}>
        {children}
    </Typography>
);
Message.displayName = 'BirthdayCard.Message';

type FormProps = ComponentPropsWithRef<'div'>;

const Form = ({ className, ref, ...props }: FormProps) => (
    <div data-slot="birthday-card-form" ref={ref} className={cn('min-w-0 flex-1 space-y-3', className)} {...props} />
);
Form.displayName = 'BirthdayCard.Form';

type FormTitleProps = ComponentPropsWithRef<'p'>;

const FormTitle = ({ className, ref, children, ...props }: FormTitleProps) => (
    <Typography
        data-slot="birthday-card-form-title"
        variant="p"
        ref={ref as never}
        className={cn('mb-2', className)}
        {...props}>
        {children}
    </Typography>
);
FormTitle.displayName = 'BirthdayCard.FormTitle';

type FormFieldsProps = ComponentPropsWithRef<'form'>;

const inputClasses = 'p-2 w-[64px] h-[48px] text-center';

const FormFields = ({ className, children, ...props }: FormFieldsProps) => (
    <form data-slot="birthday-card-form-fields" className={cn('flex items-center gap-2', className)} {...props}>
        {children ?? (
            <>
                <SingleInput
                    aria-label="Day"
                    placeholder="DD"
                    inputMode="numeric"
                    maxLength={2}
                    className={cn(inputClasses)}
                />
                <SingleInput
                    aria-label="Month"
                    placeholder="MM"
                    inputMode="numeric"
                    maxLength={2}
                    className={cn(inputClasses)}
                />
                <SingleInput
                    aria-label="Year"
                    placeholder="YYYY"
                    inputMode="numeric"
                    maxLength={4}
                    className={cn(inputClasses)}
                />
            </>
        )}
    </form>
);
FormFields.displayName = 'BirthdayCard.FormFields';

type ErrorMessageProps = ComponentPropsWithRef<'p'>;

const ErrorMessage = ({ className, ref, children, ...props }: ErrorMessageProps) => (
    <Typography
        data-slot="birthday-card-error"
        as="p"
        variant="p"
        ref={ref as never}
        className={cn('basis-full text-destructive text-[12px] font-light tracking-[0.24px] mt-2', className)}
        {...props}>
        {children}
    </Typography>
);
ErrorMessage.displayName = 'BirthdayCard.ErrorMessage';

type CtaProps = Omit<ButtonProps, 'children' | 'variant' | 'price' | 'originalPrice'> & {
    children: ReactNode;
};

const Cta = ({ className, children, ...props }: CtaProps) => (
    <Button data-slot="birthday-card-cta" className={cn('w-full mt-2', className)} {...props}>
        {children}
    </Button>
);
Cta.displayName = 'BirthdayCard.Cta';

type ToggleControlledProps = {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    defaultChecked?: never;
};

type ToggleUncontrolledProps = {
    checked?: never;
    onCheckedChange?: (checked: boolean) => void;
    defaultChecked?: boolean;
};

type ToggleProps = ComponentPropsWithRef<'div'> &
    (ToggleControlledProps | ToggleUncontrolledProps) & {
        id?: string;
        toggleProps?: Omit<ToggleUiProps, 'checked' | 'onCheckedChange' | 'aria-label'>;
        ariaLabel?: string;
        children: ReactNode;
    };

const Toggle = ({
    className,
    ref,
    checked,
    onCheckedChange,
    defaultChecked = false,
    toggleProps,
    children,
    ariaLabel = 'Turn on Emails',
    id,
    ...props
}: ToggleProps) => {
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const resolvedChecked = isControlled ? checked : internalChecked;

    const handleCheckedChange = (nextChecked: boolean) => {
        if (!isControlled) {
            setInternalChecked(nextChecked);
        }
        onCheckedChange?.(nextChecked);
    };

    return (
        <div
            data-slot="birthday-card-toggle"
            ref={ref}
            className={cn('flex justify-between basis-full mt-2', className)}
            {...props}>
            <Typography as="span" variant="p">
                {children}
            </Typography>
            <ToggleUI
                {...toggleProps}
                id={id}
                aria-label={ariaLabel}
                checked={resolvedChecked}
                onCheckedChange={handleCheckedChange}
            />
        </div>
    );
};
Toggle.displayName = 'BirthdayCard.Toggle';

const BirthdayCard = {
    Root,
    Title,
    Content,
    Image,
    Message,
    Form,
    FormTitle,
    FormFields,
    ErrorMessage,
    Cta,
    Toggle,
} as const;

export { BirthdayCard };
